/**
 * TransactionsTab (Tab 3) — Broker Portal transaction management.
 *
 * Split-panel: left is the transaction list with pipeline + compliance
 * filters, right is a profile header + tabs [Overview / Parties / Documents
 * / Timeline]. Absorbs the old components/broker-portal/TransactionManagement
 * and BrokerTransactionDetailsWrapper.
 *
 * Backend: uses the existing /api/transactions and /api/transactions/:id
 * routes (already in production, no new endpoints).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract,
  faHome,
  faUser,
  faCalendarAlt,
  faDollarSign,
  faMapMarkerAlt,
  faCheckCircle,
  faTriangleExclamation,
  faCircleExclamation,
  faUsers,
  faClockRotateLeft,
  faFolderOpen,
  faChevronLeft,
  faListUl,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../../helpers/api';
import {
  SplitPanel,
  SearchBar,
  FilterPills,
  ListItem,
  ProfileHeader,
  DetailTabs,
  colors,
  fonts,
} from '../shared';
// KOPS-654: reuse the real broker transaction tools (commission approval/override + closing/sign-off).
// These are the same components the old broker portal used; rendered here in broker mode and wrapped
// in .deal-theme so the bootstrap UI picks up the navy/gold skin. (Mushfiq to polish styling later.)
import CommissionDisbursementAuthorization from '../../../components/agent-portal/CommissionDisbursementAuthorization';
import TransactionClosingManager from '../../../components/broker-portal/TransactionClosingManager';
import CommissionPane from '../../../components/broker-portal/CommissionPane';
// KOPS-654: use the SAME document components the agent portal uses, so the broker sees identical
// document logic — BrokerReviewStatus with portal="broker" turns on the broker review/approve/stamp.
import DocumentChecklistWithUploads from '../../../components/agent-portal/DocumentChecklistWithUploads';
import BrokerReviewStatus from '../../../components/agent-portal/BrokerReviewStatus';
import '../../agent-portal/deal-theme.css';
import { faKey } from '@fortawesome/free-solid-svg-icons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TransactionListItem {
  id: string;
  file_number?: string;
  type?: string;
  stage?: string;
  client_name?: string;
  client_email?: string;
  agent_id?: string;
  brokerage_id?: string;
  compliance_status?: string;
  compliance_score?: number | string;
  created_at?: string;
  updated_at?: string;
  closed_at?: string;
  property?: {
    address?: string;
    price?: number | string;
    city?: string;
    state?: string;
  } | null;
  property_data?: any;
  documents_count?: number;
}

interface TransactionDetail extends TransactionListItem {
  parties?: Array<{
    id: string;
    full_name?: string;
    email?: string;
    phone?: string;
    role?: string;
    is_primary?: boolean;
  }>;
  documents?: Array<{
    id: string;
    document_name?: string;
    document_filename?: string;
    status?: string;
    review_status?: string;
    created_at?: string;
    s3_url?: string;
  }>;
  notes?: string;
  closing_notes?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STAGE_OPTIONS = [
  { key: 'all',            label: 'All' },
  { key: 'pre_contract',   label: 'New' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'inspection',     label: 'Inspection' },
  { key: 'appraisal',      label: 'Appraisal' },
  { key: 'closing',        label: 'Closing' },
  { key: 'closed',         label: 'Closed' },
];

const COMPLIANCE_OPTIONS = [
  { key: 'all',       label: 'All' },
  { key: 'compliant', label: 'Clean' },
  { key: 'pending',   label: 'Pending' },
  { key: 'violation', label: 'Flagged' },
];

const STAGE_COLORS: Record<string, string> = {
  pre_contract:   colors.muted,
  under_contract: colors.blue,
  inspection:     colors.orange,
  appraisal:      colors.gold2,
  closing:        colors.gold,
  closed:         colors.green,
};

const STAGE_LABELS: Record<string, string> = {
  pre_contract:   'New',
  under_contract: 'Under Contract',
  inspection:     'Inspection',
  appraisal:      'Appraisal',
  closing:        'Closing',
  closed:         'Closed',
};

const DETAIL_TABS = [
  { key: 'overview',   label: 'Overview',   icon: faHome },
  { key: 'parties',    label: 'Parties',    icon: faUsers },
  { key: 'documents',  label: 'Documents',  icon: faFolderOpen },
  { key: 'commission', label: 'Commission', icon: faDollarSign }, // KOPS-654: broker commission approval/override (CDA)
  { key: 'closing',    label: 'Closing',    icon: faKey },        // KOPS-654: broker closing + sign-off (receipts, expenses, distribution, mark-completed, 1099)
  { key: 'timeline',   label: 'Timeline',   icon: faClockRotateLeft },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TransactionsTabProps {
  brokerageId?: string;
}

const TransactionsTab: React.FC<TransactionsTabProps> = () => {
  const [transactions, setTransactions] = useState<TransactionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');

  const [selected, setSelected] = useState<TransactionListItem | null>(null);
  const [detail, setDetail] = useState<TransactionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [listCollapsed, setListCollapsed] = useState(false); // KOPS-654: hide the list for full-width detail

  // ---- List fetch ----------------------------------------------------------
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/transactions', {
        params: stageFilter !== 'all' ? { stage: stageFilter } : {},
      });
      if (res.data?.success) {
        setTransactions(res.data.transactions || []);
      } else {
        setError(res.data?.error || 'Failed to load transactions');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [stageFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // ---- Detail fetch --------------------------------------------------------
  // Reusable reload (passed to the broker tools as onUpdate so they refresh the deal after an
  // approval / closing change). Does NOT blank the panel.
  const reloadDetail = useCallback(async () => {
    if (!selected) return;
    try {
      const res = await axiosInstance.get(`/transactions/${selected.id}`);
      if (res.data?.success) setDetail(res.data.transaction);
    } catch {
      /* keep the current detail on a transient error */
    }
  }, [selected]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    const loadDetail = async () => {
      setDetailLoading(true);
      setDetail(null);
      try {
        const res = await axiosInstance.get(`/transactions/${selected.id}`);
        if (!cancelled && res.data?.success) {
          setDetail(res.data.transaction);
        }
      } catch (err) {
        if (!cancelled) {
          // Fall back to the list item shape so user still sees something
          setDetail(selected as TransactionDetail);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selected]);

  // ---- Derived: filter + search -------------------------------------------
  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const hit =
          t.file_number?.toLowerCase().includes(q) ||
          t.client_name?.toLowerCase().includes(q) ||
          t.property?.address?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (complianceFilter !== 'all' && t.compliance_status !== complianceFilter) return false;
      return true;
    });
  }, [transactions, search, complianceFilter]);

  // ---- Stats bar -----------------------------------------------------------
  const stats = useMemo(() => {
    const total = transactions.length;
    const active = transactions.filter((t) => t.stage && t.stage !== 'closed').length;
    const clean = transactions.filter((t) => t.compliance_status === 'compliant').length;
    const flagged = transactions.filter((t) => t.compliance_status === 'violation').length;
    return { total, active, clean, flagged };
  }, [transactions]);

  // ---- Render --------------------------------------------------------------
  const detailPanel = (
    <TransactionDetailPanel
      transaction={detail || (selected as TransactionDetail)}
      loading={detailLoading}
      activeTab={activeDetailTab}
      onTabChange={setActiveDetailTab}
      onReload={reloadDetail}
    />
  );

  const listPanel = (
    <>
      <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
        <div style={{ marginBottom: '8px' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search transactions..." />
        </div>
        <div style={{ marginBottom: '6px' }}>
          <FilterPills options={STAGE_OPTIONS} activeKey={stageFilter} onChange={setStageFilter} />
        </div>
        <FilterPills options={COMPLIANCE_OPTIONS} activeKey={complianceFilter} onChange={setComplianceFilter} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>
            Loading transactions…
          </div>
        ) : error ? (
          <div style={{ padding: '20px', color: colors.red, fontSize: '12px' }}>{error}</div>
        ) : filteredTransactions.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
            <FontAwesomeIcon icon={faFileContract} style={{ fontSize: '32px', marginBottom: '8px' }} />
            <div style={{ fontSize: '13px' }}>
              {transactions.length === 0
                ? 'No transactions yet'
                : 'No transactions match current filters'}
            </div>
          </div>
        ) : (
          filteredTransactions.map((t) => (
            <ListItem
              key={t.id}
              selected={selected?.id === t.id}
              onClick={() => {
                setSelected(t);
                setActiveDetailTab('overview');
                setListCollapsed(true); // give the detail full width on open
              }}
              title={t.property?.address || t.client_name || t.file_number || '(no address)'}
              subtitle={t.client_name ? `Client: ${t.client_name}` : t.type}
              meta={
                <span>
                  {t.file_number ? `#${t.file_number} · ` : ''}
                  {formatRelativeTime(t.updated_at || t.created_at)}
                </span>
              }
              rightContent={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                  <StageBadge stage={t.stage} />
                  <ComplianceIcon status={t.compliance_status} />
                </div>
              }
            />
          ))
        )}
      </div>
    </>
  );

  return (
    <>
      {/* KOPS-654: hide the stats bar while focused on a single transaction (full-width detail) */}
      {!(selected && listCollapsed) && <StatsBar stats={stats} loading={loading} />}

      {selected && listCollapsed ? (
        // KOPS-654: full-width detail — list hidden for maximum real estate.
        // background: offBg (light) so it matches the split view and doesn't fall through to the
        // dark navy hub page background.
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: colors.offBg }}>
          <div style={{ padding: '8px 16px', borderBottom: `1px solid ${colors.border}`, background: colors.offBg, flexShrink: 0 }}>
            <button
              onClick={() => setListCollapsed(false)}
              style={{ background: colors.white, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600, color: colors.navy, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <FontAwesomeIcon icon={faListUl} />
              Show transaction list
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: colors.offBg }}>
            {detailPanel}
          </div>
        </div>
      ) : (
        <SplitPanel
          left={listPanel}
          right={
            selected ? (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{detailPanel}</div>
            ) : (
              <EmptyDetailState />
            )
          }
        />
      )}
    </>
  );
};

// ===========================================================================
// Stats bar
// ===========================================================================

const StatsBar: React.FC<{ stats: { total: number; active: number; clean: number; flagged: number }; loading: boolean }> = ({ stats, loading }) => (
  <div
    style={{
      background: colors.offBg,
      borderBottom: `1px solid ${colors.border}`,
      padding: '16px 24px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '12px',
      flexShrink: 0,
    }}
  >
    <StatCard icon={faFileContract}        label="Total Transactions" value={loading ? '—' : stats.total}   color={colors.blue} />
    <StatCard icon={faCalendarAlt}         label="Active"             value={loading ? '—' : stats.active}  color={colors.gold2} />
    <StatCard icon={faCheckCircle}         label="Compliant"          value={loading ? '—' : stats.clean}   color={colors.green} />
    <StatCard icon={faTriangleExclamation} label="Flagged"            value={loading ? '—' : stats.flagged} color={colors.red} />
  </div>
);

const StatCard: React.FC<{ icon: any; label: string; value: React.ReactNode; color: string }> = ({ icon, label, value, color }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: '8px',
      padding: '14px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    }}
  >
    <div
      style={{
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: `${color}22`,
        border: `2px solid ${color}55`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: '14px' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: fonts.display, fontSize: '24px', color: colors.navy, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: colors.navy, marginTop: '2px' }}>{label}</div>
    </div>
  </div>
);

// ===========================================================================
// Right panel — transaction detail
// ===========================================================================

interface TransactionDetailPanelProps {
  transaction: TransactionDetail;
  loading: boolean;
  activeTab: string;
  onTabChange: (k: string) => void;
  onReload: () => void;
}

const TransactionDetailPanel: React.FC<TransactionDetailPanelProps> = ({
  transaction,
  loading,
  activeTab,
  onTabChange,
  onReload,
}) => {
  const propertyAddr = transaction.property?.address;
  const propertyPrice = transaction.property?.price;

  return (
    // KOPS-654: center the detail in a ~1150px column (matches the agent deal view) so content
    // doesn't stretch edge-to-edge on wide screens.
    <div style={{ maxWidth: 1150, margin: '0 auto' }}>
      <ProfileHeader
        title={propertyAddr || transaction.client_name || transaction.file_number || 'Transaction'}
        metaItems={[
          ...(transaction.client_name ? [{ icon: faUser, text: transaction.client_name }] : []),
          ...(propertyPrice
            ? [{ icon: faDollarSign, text: `$${Number(propertyPrice).toLocaleString()}` }]
            : []),
          ...(transaction.file_number ? [{ text: `File #${transaction.file_number}` }] : []),
          { icon: faCalendarAlt, text: `Updated ${formatRelativeTime(transaction.updated_at || transaction.created_at)}` },
        ]}
        badges={
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <StageBadge stage={transaction.stage} />
            <ComplianceBadge status={transaction.compliance_status} />
          </div>
        }
      />

      <DetailTabs tabs={DETAIL_TABS} activeTab={activeTab} onChange={onTabChange} />

      <div style={{ padding: '0 24px 24px 24px' }}>
        {loading && (
          <div style={{ padding: '20px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
            Loading details…
          </div>
        )}
        {activeTab === 'overview' && <OverviewPane transaction={transaction} />}
        {activeTab === 'parties' && <PartiesPane transaction={transaction} />}
        {activeTab === 'documents' && <DocumentsPane transaction={transaction} onReload={onReload} />}
        {activeTab === 'commission' && <CommissionPane transaction={transaction} onReload={onReload} portal="broker" />}
        {activeTab === 'closing' && (
          <div className="deal-theme">
            <TransactionClosingManager transaction={transaction} onUpdate={onReload} />
          </div>
        )}
        {activeTab === 'timeline' && <TimelinePane transaction={transaction} />}
      </div>
    </div>
  );
};

// ---- Panes -----------------------------------------------------------------

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        color: colors.navy,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '10px',
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const KeyValue: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '6px 0',
      borderBottom: `1px solid ${colors.border}`,
      fontSize: '13px',
    }}
  >
    <span style={{ color: colors.muted }}>{k}</span>
    <span style={{ color: colors.navy, fontWeight: 500, textAlign: 'right' }}>{v}</span>
  </div>
);

const OverviewPane: React.FC<{ transaction: TransactionDetail }> = ({ transaction }) => {
  const prop = transaction.property || transaction.property_data || {};
  return (
    <>
      <SectionCard title="Property">
        <KeyValue k="Address" v={(prop as any).address || '—'} />
        <KeyValue
          k="Price"
          v={(prop as any).price ? `$${Number((prop as any).price).toLocaleString()}` : '—'}
        />
        <KeyValue k="City" v={(prop as any).city || '—'} />
        <KeyValue k="State" v={(prop as any).state || '—'} />
      </SectionCard>

      <SectionCard title="Transaction">
        <KeyValue k="File #" v={transaction.file_number || '—'} />
        <KeyValue k="Type" v={transaction.type || '—'} />
        <KeyValue k="Stage" v={<StageBadge stage={transaction.stage} />} />
        <KeyValue k="Compliance" v={<ComplianceBadge status={transaction.compliance_status} />} />
        {transaction.compliance_score != null && (
          <KeyValue k="Compliance score" v={`${Number(transaction.compliance_score)}/100`} />
        )}
        <KeyValue k="Client" v={transaction.client_name || '—'} />
        <KeyValue k="Created" v={formatAbsoluteTime(transaction.created_at)} />
        {transaction.closed_at && <KeyValue k="Closed" v={formatAbsoluteTime(transaction.closed_at)} />}
      </SectionCard>

      {transaction.notes && (
        <SectionCard title="Notes">
          <div style={{ fontSize: '13px', color: colors.navy, whiteSpace: 'pre-wrap' }}>
            {transaction.notes}
          </div>
        </SectionCard>
      )}
    </>
  );
};

const PartiesPane: React.FC<{ transaction: TransactionDetail }> = ({ transaction }) => {
  const parties = transaction.parties || [];
  return (
    <SectionCard title={`Parties (${parties.length})`}>
      {parties.length === 0 ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
          No parties linked to this transaction
        </div>
      ) : (
        parties.map((p) => (
          <div
            key={p.id}
            style={{
              padding: '10px 0',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>
                {p.full_name || '(unnamed)'}
                {p.is_primary && (
                  <span
                    style={{
                      marginLeft: '8px',
                      fontSize: '9px',
                      background: `${colors.gold}22`,
                      color: colors.gold,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}
                  >
                    Primary
                  </span>
                )}
              </div>
              {p.email && (
                <div style={{ fontSize: '12px', color: colors.muted }}>{p.email}</div>
              )}
              {p.phone && <div style={{ fontSize: '12px', color: colors.muted }}>{p.phone}</div>}
            </div>
            <span
              style={{
                fontSize: '10px',
                fontWeight: 700,
                background: `${colors.blue}22`,
                color: colors.blue,
                padding: '3px 8px',
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {p.role || 'party'}
            </span>
          </div>
        ))
      )}
    </SectionCard>
  );
};

// KOPS-654: identical to the agent portal Documents tab — same components, same endpoint
// (/transactions/:id/documents). Only difference: BrokerReviewStatus runs in portal="broker"
// mode so the broker gets review / approve / reject / stamp actions.
const dealRoleFor = (deal: any): string => {
  const type = deal?.type?.toLowerCase();
  const clientType = deal?.client_type?.toLowerCase();
  if (type === 'purchase') return clientType === 'seller' ? 'seller_agent' : 'buyer_agent';
  if (type === 'listing' || type === 'sale') return clientType === 'buyer' ? 'buyer_agent' : 'listing_agent';
  if (type === 'referral') return 'referring_agent';
  return clientType === 'buyer' ? 'buyer_agent' : 'listing_agent';
};

const DocumentsPane: React.FC<{ transaction: TransactionDetail; onReload: () => void }> = ({ transaction }) => {
  const txId = transaction.id;
  const [dealDocuments, setDealDocuments] = useState<any[]>([]);
  const [checklistRefresh, setChecklistRefresh] = useState(0);
  const [brokerReviewRefresh, setBrokerReviewRefresh] = useState(0);

  const fetchDocs = useCallback(async () => {
    if (!txId) return;
    try {
      const r = await axiosInstance.get(`/transactions/${txId}/documents`);
      setDealDocuments(r.data?.documents || []);
    } catch {
      setDealDocuments([]);
    }
  }, [txId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs, checklistRefresh]);

  // Same upload flow as the agent portal (handleChecklistDocumentUpload).
  const handleUpload = async (document: any, file: File, existingDocumentId?: string) => {
    if (!txId) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', document.type || 'transaction_document');
      fd.append('description', document.name || file.name);
      fd.append('checklist_name', document.name || '');
      fd.append('owner_type', 'user');
      fd.append('transaction_id', txId);
      if (transaction.brokerage_id) fd.append('brokerage_id', transaction.brokerage_id);
      if (existingDocumentId) fd.append('replaces_document_id', existingDocumentId);
      const up = await axiosInstance.post('/transaction-documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (!up.data?.success || !up.data?.document) throw new Error(up.data?.error || 'Upload failed');
      const documentId = up.data.document.id || up.data.document.document_id;
      if (!existingDocumentId && documentId) {
        await axiosInstance.post(`/transactions/${txId}/documents`, { document_id: documentId, document_role: 'required' });
      }
      setChecklistRefresh((p) => p + 1);
    } catch (err) {
      console.error('Document upload failed:', err);
    }
  };

  return (
    <div className="deal-theme">
      <DocumentChecklistWithUploads
        transactionId={txId}
        transactionType={transaction.type || ''}
        transactionRole={dealRoleFor(transaction)}
        transactionStage={transaction.stage}
        existingDocuments={dealDocuments}
        onDocumentUpload={handleUpload}
        brokerageId={transaction.brokerage_id}
        refreshTrigger={checklistRefresh}
      />
      <BrokerReviewStatus
        transactionId={txId}
        refreshTrigger={brokerReviewRefresh}
        portal="broker"
        onDocumentReviewed={() => { setBrokerReviewRefresh((p) => p + 1); setChecklistRefresh((p) => p + 1); }}
      />
    </div>
  );
};

// CommissionPane imported from ../../../components/broker-portal/CommissionPane

const TimelinePane: React.FC<{ transaction: TransactionDetail }> = ({ transaction }) => (
  <SectionCard title="Timeline">
    <TimelineEvent
      when={transaction.created_at}
      label="Transaction created"
      icon={faFileContract}
      color={colors.blue}
    />
    {transaction.updated_at && transaction.updated_at !== transaction.created_at && (
      <TimelineEvent
        when={transaction.updated_at}
        label={`Stage: ${STAGE_LABELS[transaction.stage || ''] || transaction.stage || '—'}`}
        icon={faClockRotateLeft}
        color={colors.gold2}
      />
    )}
    {transaction.closed_at && (
      <TimelineEvent
        when={transaction.closed_at}
        label="Transaction closed"
        icon={faCheckCircle}
        color={colors.green}
      />
    )}
  </SectionCard>
);

const TimelineEvent: React.FC<{ when?: string; label: string; icon: any; color: string }> = ({ when, label, icon, color }) => (
  <div
    style={{
      display: 'flex',
      gap: '12px',
      padding: '10px 0',
      borderBottom: `1px solid ${colors.border}`,
      alignItems: 'flex-start',
    }}
  >
    <div
      style={{
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        background: `${color}22`,
        border: `2px solid ${color}55`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: '11px' }} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.navy }}>{label}</div>
      {when && (
        <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>
          {formatAbsoluteTime(when)}
        </div>
      )}
    </div>
  </div>
);

// ===========================================================================
// Helpers / small components
// ===========================================================================

const StageBadge: React.FC<{ stage?: string }> = ({ stage }) => {
  const color = STAGE_COLORS[stage || ''] || colors.muted;
  const label = STAGE_LABELS[stage || ''] || stage || 'Unknown';
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const ComplianceBadge: React.FC<{ status?: string }> = ({ status }) => {
  const color =
    status === 'compliant' ? colors.green :
    status === 'violation' ? colors.red :
    status === 'approved' ? colors.green :
    colors.orange;
  const label = status || 'unknown';
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </span>
  );
};

const ComplianceIcon: React.FC<{ status?: string }> = ({ status }) => {
  if (status === 'compliant' || status === 'approved') {
    return <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '11px' }} />;
  }
  if (status === 'violation') {
    return <FontAwesomeIcon icon={faCircleExclamation} style={{ color: colors.red, fontSize: '11px' }} />;
  }
  return <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: colors.orange, fontSize: '11px' }} />;
};

function statusChipColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('approv')) return colors.green;
  if (s.includes('reject') || s.includes('denied')) return colors.red;
  if (s.includes('pending') || s.includes('review')) return colors.orange;
  return colors.muted;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function formatAbsoluteTime(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

const EmptyDetailState: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '48px',
      color: colors.muted,
    }}
  >
    <FontAwesomeIcon icon={faFileContract} style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold3 }} />
    <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>Select a transaction</div>
    <div style={{ fontSize: '12px', marginTop: '4px', textAlign: 'center' }}>
      Choose a transaction from the list to view property, parties,<br />
      documents, and timeline
    </div>
  </div>
);

export default TransactionsTab;
